import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";

import { useUser } from "../../user/utils/user";

import { AgGridWithOverlay } from "../../common/components/AgGridWithOverlay";
import LoadingModal from "../../common/components/LoadingModal.js";
import DateInput from "../../common/components/form/DateInput.js";
import Select from "../../common/components/form/Select.js";
import { useRoleClientNavigate } from "../../common/hooks/useRoleClientNavigate";
import { get } from "../../common/utils/api";
import { calculatePercentage, formatDate } from "../../common/utils/helpers";
import { questionCodeMapper } from "./ReviewDetail";

function SummaryForm({ groups, countries, t }) {
  const { user } = useUser();

  return (
    <React.Fragment>
      <div className="space-y-6 pt-8 sm:space-y-5 sm:pt-10">
        <div className="space-y-6 sm:space-y-5">
          <DateInput label={"Start date"} name={"formStartDate"} />
          <DateInput label={"End date"} name={"formEndDate"} />
          {user.user.is_supervisor && (
            <>
              <Select label={"Group"} name={"formGroup"} options={groups} />
              <Select
                label={"Country"}
                name={"formCountry"}
                options={countries}
              />
            </>
          )}
        </div>
        <div>
          <div className="flex justify-end">
            <button
              type="submit"
              className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
            >
              {t("common.generate", "Generate Report")}
            </button>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

const groupByCrop = (data) => {
  const groupedData = data.reduce((acc, item) => {
    const { username, crop, id } = item;

    if (!acc[username]) {
      acc[username] = {
        username,
        crops: {},
      };
    }

    // Track unique ids to avoid duplicate counting
    const uniqueIds = new Set(acc[username].uniqueIds || []);
    if (uniqueIds.has(id)) {
      return acc;
    }
    uniqueIds.add(id);
    acc[username].uniqueIds = uniqueIds;

    // Aggregate crops
    if (!acc[username].crops[crop]) {
      acc[username].crops[crop] = 1;
    }
    acc[username].crops[crop]++;

    return acc;
  }, {});

  let user_data = Object.values(groupedData).map((item) => ({
    username: item.username,
    ...item.crops,
  }));
  // Sort user_data by username
  return user_data.sort((a, b) => a.username.localeCompare(b.username));
};

function Summary({ t }) {
  const [groupOptions, setGroupOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [summaryData, setSummaryData] = useState({});
  const [cropColumnDefs, setCropColumnDefs] = useState([]);
  const [error, setError] = useState(null);

  const navigate = useRoleClientNavigate();
  const formRef = useRef();

  // Default date range: past three days
  let today = new Date();
  let daysBefore = new Date();
  daysBefore.setDate(today.getDate() - 30);

  let userDetailsColumnDefsByUsername = [
    { field: "username", headerName: t("user.email", "Username") },
    {
      field: "parcel_count",
      headerName: t("common.reviewed", "# Parcels"),
      type: "numericColumn",
    },
    {
      field: "reviewed_count",
      headerName: t("common.reviewed", "# Reviewed"),
      type: "numericColumn",
    },
    {
      field: "approved_count",
      headerName: t("common.approved", "# Approved"),
      type: "numericColumn",
    },
    {
      field: "approved_percent",
      headerName: t("common.approved", "% Approved"),
      type: "numericColumn",
      valueFormatter: (params) => params.data.approved_percent.toFixed(2),
    },
  ];

  let userDetailsColumnDefsByDate = [
    { field: "username", headerName: t("user.email", "Username") },
    {
      field: "date",
      headerName: t("common.total", "Date"),
      type: "numericColumn",
    },
    {
      field: "parcel_count",
      headerName: t("common.reviewed", "# Parcels"),
      type: "numericColumn",
    },
    {
      field: "reviewed_count",
      headerName: t("common.reviewed", "# Reviewed"),
      type: "numericColumn",
      cellRenderer: (params) => {
        return params.data.reviewed_count === 0 ? (
          <span>{0}</span>
        ) : (
          <span className="text-cyan-600 hover:text-cyan-900 cursor-pointer">
            {params.value}
          </span>
        );
      },
      onCellClicked(params) {
        if (params.data.reviewed_count > 0) {
          navigate(
            `/review/parcel?user=${params.data.username}&date=${params.data.date}`,
          );
        }
      },
      cellClass: ["text-right", "cursor-pointer"],
    },
    {
      field: "approved_count",
      headerName: t("common.approved", "# Approved"),
      type: "numericColumn",
    },
    {
      field: "approved_percent",
      headerName: t("common.approved", "% Approved"),
      type: "numericColumn",
      valueFormatter: (params) => {
        params.data.approved_percent.toFixed(2);
      },
    },
  ];

  let userDetailsColumnDefsByQuestions = [
    { field: "username", headerName: t("user.email", "Username") },
    {
      field: "question_code",
      headerName: t("common.Question", "Question"),
      type: "numericColumn",
      tooltipField: "question_code_decoded",
      cellClass: ["text-right", "cursor-pointer"],
    },
    {
      field: "rejected_count",
      headerName: t("common.rejected", "# Rejected"),
      type: "numericColumn",
    },
    {
      field: "rejected_percent",
      headerName: t("common.rejected", "% Rejected"),
      type: "numericColumn",
      valueFormatter: (params) => params.data.rejected_percent.toFixed(2),
    },
  ];
  const reshapeCrop = (data = []) => {
    const result = {};
    data.forEach((item) => {
      const { username, crop, count } = item;

      if (!result[username]) {
        result[username] = { username };
      }

      result[username][crop] = count;
    });
    // Convert object to array
    return Object.values(result);
  };

  const generateColumnDefs = (data = []) => {
    if (!Array.isArray(data)) return [];

    const uniqueCrops = new Set();

    data.forEach((item) => {
      uniqueCrops.add(item.crop);
    });

    const columnDefs = [{ field: "username", headerName: "Username" }];

    uniqueCrops.forEach((crop) => {
      columnDefs.push({
        field: crop,
        headerName: `# ${crop}`,
        type: "numericColumn",
        valueGetter: (params) => params.data[crop] || 0,
      });
    });

    return columnDefs;
  };
  const getSummaryData = async (
    start_date,
    end_date,
    group,
    country,
    setSubmitting,
  ) => {
    get(
      `/data/summary/?start_date=${start_date}&end_date=${end_date}&group=${group}&country=${country}&username=All`,
    )
      .then((response) => {
        return response.data;
      })
      .then((data) => {
        const reshapedCropData = reshapeCrop(data.by_crop);
        const overall = data.overall.map((item) => ({
          ...item,
          approved_percent: calculatePercentage(
            item.approved_count,
            item.reviewed_count,
          ),
        }));
        const daily = data.daily.map((item) => ({
          ...item,
          approved_percent: calculatePercentage(
            item.approved_count,
            item.reviewed_count,
          ),
        }));
        const mistakes = data.mistakes.map((item) => {
          return {
            ...item,
            question_code_decoded: questionCodeMapper[item.question_code],
            rejected_percent: calculatePercentage(
              item.rejected_count,
              item.reviewed_count,
            ),
          };
        });
        setSummaryData({
          userDetailsGroupedByUsername: overall,
          userDetailsGroupedByDate: daily,
          userDetailsGroupedByQuestions: mistakes,
          userDetailsGroupedByCrop: reshapedCropData,
        });
        const cropColumnDefsDynamic = generateColumnDefs(data.by_crop);
        setCropColumnDefs(cropColumnDefsDynamic);
      })
      .catch((error) => {
        console.error(error);
        setError(error);
      })
      .finally(() => setTimeout(() => setSubmitting(false), 300));
  };
  const gridUsername = useRef(null);
  const gridDate = useRef(null);
  const gridQuestions = useRef(null);
  const gridCrop = useRef(null);

  const onGridReady = (params, grid) => {
    grid.current = params.api;
    params.api.sizeColumnsToFit();
  };

  const exportData = (grid) => {
    grid.current.exportDataAsCsv({
      allColumns: true,
      processCellCallback: (v) =>
        v.column.colDef.type === "combinedNumericColumn" ? v.value[0] : v.value,
    });
  };

  function onSubmit(value, { setSubmitting }) {
    getSummaryData(
      value.formStartDate,
      value.formEndDate,
      value.formGroup,
      value.formCountry,
      setSubmitting,
    );
    sessionStorage.setItem("startDate", value.formStartDate);
    sessionStorage.setItem("endDate", value.formEndDate);
    sessionStorage.setItem("group", value.formGroup);
    sessionStorage.setItem("country", value.formCountry);
  }
  const initialValues = {
    formStartDate:
      sessionStorage.getItem("startDate") || formatDate(daysBefore),
    formEndDate: sessionStorage.getItem("endDate") || formatDate(today),
    formGroup: sessionStorage.getItem("group") || "All",
    formCountry: sessionStorage.getItem("country") || "All",
  };

  const getGroups = async () => {
    get("/users/groups/?new_review=true").then((response) => {
      setGroupOptions(["All", ...response.data]);
    });
  };
  const getCountries = async () => {
    get("/users/countries/").then((response) => {
      setCountryOptions(["All", ...response.data.map((c) => c.name)]);
    });
  };
  useEffect(() => {
    getGroups();
    getCountries();
  }, []);

  return (
    <React.Fragment>
      <div className="mx-auto max-w-2xl px-4 sm:px-6 bg-white shadow rounded-md py-4 sm:py-6">
        <div>
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {t("common.summary", "Generate Summary Table")}
          </h3>
        </div>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          innerRef={formRef}
        >
          {({ isSubmitting }) => (
            <React.Fragment>
              <LoadingModal open={isSubmitting} />
              <Form className="space-y-8 divide-y divide-gray-200">
                <SummaryForm
                  groups={groupOptions}
                  countries={countryOptions}
                  t={t}
                />
              </Form>
            </React.Fragment>
          )}
        </Formik>
      </div>
      <div className="mt-16">
        <div className="space-y-8">
          <div className="sm:flex-auto">
            <p className="text-lg font-bold text-center text-gray-900 tracking-tight">
              {t("common.numberOfRecordsByUser", "Overall User Activity")}
            </p>
            <div data-testid="summaryByUsername">
              <div className="mb-2 flex justify-end">
                <button
                  onClick={() => exportData(gridUsername)}
                  className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
                >
                  {t("common.exportToCSV", "Export to CSV")}
                </button>
              </div>
              <div className="mt-2 ag-theme-alpine" style={{ height: 400 }}>
                <AgGridWithOverlay
                  ref={gridUsername}
                  rowData={summaryData.userDetailsGroupedByUsername}
                  // columnTypes={{ combinedNumericColumn: {} }}
                  columnDefs={userDetailsColumnDefsByUsername}
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                  }}
                  onGridReady={(params) => onGridReady(params, gridUsername)}
                  error={error}
                  t={t}
                />
              </div>
            </div>
          </div>
          <div className="sm:flex-auto">
            <p className="text-lg font-bold text-center text-gray-900 tracking-tight">
              {t(
                "common.numberOfRecordsForEachUserByDate",
                "Daily User Activity",
              )}
            </p>
            <div data-testid="summaryByDate">
              <div className="mb-2 flex justify-end">
                <button
                  onClick={() => exportData(gridDate)}
                  className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
                >
                  {t("common.exportToCSV", "Export to CSV")}
                </button>
              </div>
              <div className="mt-2 ag-theme-alpine" style={{ height: 400 }}>
                <AgGridWithOverlay
                  ref={gridDate}
                  rowData={summaryData.userDetailsGroupedByDate}
                  // columnTypes={{ combinedNumericColumn: {} }}
                  columnDefs={userDetailsColumnDefsByDate}
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                  }}
                  onGridReady={(params) => onGridReady(params, gridDate)}
                  error={error}
                  t={t}
                />
              </div>
            </div>
          </div>
          <div className="sm:flex-auto">
            <p className="text-lg font-bold text-center text-gray-900 tracking-tight">
              {t(
                "common.numberOfRecordsByUserByQuestion",
                "Most Common Mistakes",
              )}
            </p>
            <div data-testid="summaryByQuestions">
              <div className="mb-2 flex justify-end">
                <button
                  onClick={() => exportData(gridQuestions)}
                  className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
                >
                  {t("common.exportToCSV", "Export to CSV")}
                </button>
              </div>
              <div className="mt-2 ag-theme-alpine" style={{ height: 400 }}>
                <AgGridWithOverlay
                  ref={gridQuestions}
                  rowData={summaryData.userDetailsGroupedByQuestions}
                  // columnTypes={{ combinedNumericColumn: {} }}
                  columnDefs={userDetailsColumnDefsByQuestions}
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                  }}
                  onGridReady={(params) => onGridReady(params, gridQuestions)}
                  error={error}
                  t={t}
                />
              </div>
            </div>
          </div>
          <div className="sm:flex-auto">
            <p className="text-lg font-bold text-center text-gray-900 tracking-tight">
              {t("common.numberOfRecordsByUserByCrop", "Breakdown by Crop")}
            </p>
            <div data-testid="summaryByCrop">
              <div className="mb-2 flex justify-end">
                <button
                  onClick={() => exportData(gridCrop)}
                  className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
                >
                  {t("common.exportToCSV", "Export to CSV")}
                </button>
              </div>
              <div className="mt-2 ag-theme-alpine" style={{ height: 400 }}>
                <AgGridWithOverlay
                  ref={gridCrop}
                  rowData={summaryData.userDetailsGroupedByCrop}
                  columnDefs={cropColumnDefs}
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                  }}
                  onGridReady={(params) => onGridReady(params, gridCrop)}
                  error={error}
                  t={t}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
export default withTranslation()(Summary);
